// ./src/hooks/useTrackingSession.ts
//
// High-level orchestrator hook for live GPS tracking.
// Composes useGpsTracker, useTrackingFilter, useTrackingUploader,
// and TrackingStorageContext into a unified tracking lifecycle.
//
// This is what UI components interact with. It exposes:
// - Session state (status, distance, speed, duration, accuracy, etc.)
// - Actions (start, pause, resume, stop, discard)
// - App restart recovery from IndexedDB
//
// The hook integrates with ActiveTravelContext for server-side
// tracking lifecycle (pause/resume/discard mutations). The start
// and stop actions are handled by the caller (ActiveTravelContext's
// startTravel with enableTracking and completeTravel), not by this hook.

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useGpsTracker, type GpsReading, type GpsError } from "./useGpsTracker";
import { useTrackingFilter } from "./useTrackingFilter";
import { useTrackingUploader } from "./useTrackingUploader";
import {
  useTrackingStorage,
  type BufferedTrackingPoint,
  type TrackingSessionState,
} from "@/contexts/TrackingStorageContext/TrackingStorageContext";
import { haversineDistance, metersToKm, mpsToKmh } from "@/utils/trackingGeoUtils";
import { DURATION_TICK_INTERVAL_MS, GPS_ACCURACY_THRESHOLD } from "@/config/trackingConstants";

// ============================================
// Types
// ============================================

export type TrackingSessionStatus = "idle" | "active" | "paused" | "restoring";

export interface TrackingSessionState_UI {
  /** Current session status */
  status: TrackingSessionStatus;
  /** The travel being tracked */
  travelId: string | null;
  /** Current segment counter */
  segmentId: number;
  /** GPS-derived distance in km */
  gpsDistance: number;
  /** Current speed in km/h from the latest GPS reading, or null */
  currentSpeed: number | null;
  /** Current heading in degrees from the latest GPS reading, or null */
  currentHeading: number | null;
  /** Seconds of active tracking (excludes pauses) */
  activeDuration: number;
  /** Number of filtered points collected so far (uploaded + buffered) */
  pointsCollected: number;
  /** Number of points waiting in the upload buffer */
  pointsBuffered: number;
  /** Current GPS accuracy in meters, or null if no reading yet */
  gpsAccuracy: number | null;
  /** Whether the uploader is currently sending a batch */
  isUploading: boolean;
  /** Current error (permission denied, GPS unavailable, etc.) or null */
  error: string | null;
  /** Whether the session was restored from IndexedDB after app restart */
  wasRestored: boolean;
}

export interface TrackingSessionActions {
  /**
   * Initialize a new tracking session for a travel.
   * Called after the travel has been created on the server with enableTracking=true.
   * Sets up GPS watcher, filter, and uploader.
   *
   * @param travelId - The travel ID from the server
   * @param segmentId - Starting segment ID (usually 0)
   */
  start: (travelId: string, segmentId: number) => Promise<void>;

  /**
   * Pause the tracking session.
   * Force-keeps the last GPS point, flushes the buffer, stops GPS watcher.
   * The caller must also call the server pause mutation.
   *
   * @returns The current gpsDistance (for the caller to update server state)
   */
  pause: () => Promise<number>;

  /**
   * Resume tracking after a pause.
   * Called after the server resume mutation returns with the new segmentId.
   * Restarts GPS watcher and uploader.
   *
   * @param segmentId - The new segment ID from the server response
   */
  resume: (segmentId: number) => Promise<void>;

  /**
   * Stop tracking completely. Flushes remaining buffer.
   * Called before the travel completion mutation.
   * Does NOT call any server mutation — the caller handles that.
   *
   * @returns The final gpsDistance
   */
  stop: () => Promise<number>;

  /**
   * Discard the tracking session. Stops everything and clears local storage.
   * The caller must also call the server discard mutation.
   */
  discard: () => Promise<void>;

  /**
   * Dismiss the restored session notification.
   */
  clearRestoredFlag: () => void;

  /**
   * Clear the current error.
   */
  clearError: () => void;
}

export interface UseTrackingSessionReturn {
  session: TrackingSessionState_UI;
  actions: TrackingSessionActions;
}

// ============================================
// Hook
// ============================================

export const useTrackingSession = (): UseTrackingSessionReturn => {
  const storage = useTrackingStorage();

  // ── React state (drives UI re-renders) ──
  // Only values that the UI needs to display are in state.
  // High-frequency values (speed, heading, accuracy) are in refs
  // and copied to state on a throttled tick.

  const [status, setStatus] = useState<TrackingSessionStatus>("idle");
  const [travelId, setTravelId] = useState<string | null>(null);
  const [segmentId, setSegmentId] = useState(0);
  const [gpsDistance, setGpsDistance] = useState(0);
  const [currentSpeed, setCurrentSpeed] = useState<number | null>(null);
  const [currentHeading, setCurrentHeading] = useState<number | null>(null);
  const [activeDuration, setActiveDuration] = useState(0);
  const [pointsCollected, setPointsCollected] = useState(0);
  const [pointsBuffered, setPointsBuffered] = useState(0);
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wasRestored, setWasRestored] = useState(false);
  const [isUploadingState, setIsUploadingState] = useState(false);

  // ── Refs for high-frequency data (no re-renders) ──

  /** Running GPS distance in km — updated on every raw reading */
  const gpsDistanceRef = useRef(0);
  /** Last raw GPS reading's position — for distance accumulation */
  const lastRawLatRef = useRef<number | null>(null);
  const lastRawLngRef = useRef<number | null>(null);
  /** Active duration in seconds */
  const activeDurationRef = useRef(0);
  /** Timestamp when active tracking last started/resumed (for duration calc) */
  const activeStartTimestampRef = useRef<number | null>(null);
  /** Total filtered points count (uploaded + buffered) */
  const pointsCollectedRef = useRef(0);
  /** Current status ref for use in callbacks */
  const statusRef = useRef<TrackingSessionStatus>("idle");
  /** Duration tick timer */
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Whether GPS watcher should be enabled */
  const [gpsEnabled, setGpsEnabled] = useState(false);
  /** Whether uploader should be enabled */
  const [uploaderEnabled, setUploaderEnabled] = useState(false);

  // ── Sync status ref ──
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // ── Duration timer ──

  const startDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
    }
    activeStartTimestampRef.current = Date.now();

    durationTimerRef.current = setInterval(() => {
      if (activeStartTimestampRef.current && statusRef.current === "active") {
        const elapsed = (Date.now() - activeStartTimestampRef.current) / 1000;
        const total = activeDurationRef.current + elapsed;
        setActiveDuration(Math.floor(total));
      }
    }, DURATION_TICK_INTERVAL_MS);
  }, []);

  const stopDurationTimer = useCallback(() => {
    // Accumulate elapsed time
    if (activeStartTimestampRef.current) {
      const elapsed = (Date.now() - activeStartTimestampRef.current) / 1000;
      activeDurationRef.current += elapsed;
      activeStartTimestampRef.current = null;
    }
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    setActiveDuration(Math.floor(activeDurationRef.current));
  }, []);

  // ── Persist session state to IndexedDB ──

  const persistSession = useCallback(async () => {
    const currentTravelId = travelId;
    if (!currentTravelId || statusRef.current === "idle") return;

    const sessionState: TrackingSessionState = {
      travelId: currentTravelId,
      status: statusRef.current === "active" ? "active" : "paused",
      segmentId,
      nextSeq: filter.getNextSeq(),
      gpsDistance: gpsDistanceRef.current,
      activeDuration: activeDurationRef.current,
      lastActiveTimestamp: activeStartTimestampRef.current ?? Date.now(),
      lastRawLat: lastRawLatRef.current,
      lastRawLng: lastRawLngRef.current,
    };

    try {
      await storage.setSessionState(sessionState);
    } catch {
      // Silent — IndexedDB persistence is best-effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelId, segmentId, storage]);

  // ── Filter callbacks ──

  const handlePointKept = useCallback((point: BufferedTrackingPoint) => {
    pointsCollectedRef.current += 1;
    setPointsCollected(pointsCollectedRef.current);
    uploader.addPoint(point);
    setPointsBuffered(uploader.getBufferSize());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Compose sub-hooks ──

  const filter = useTrackingFilter({
    onPointKept: handlePointKept,
  });

  // ── GPS reading handler ──

  const handleGpsReading = useCallback((reading: GpsReading) => {
    if (statusRef.current !== "active") return;

    // Distance accumulation: add haversine distance from last raw reading
    if (lastRawLatRef.current != null && lastRawLngRef.current != null) {
      const dist = haversineDistance(
        { lat: lastRawLatRef.current, lng: lastRawLngRef.current },
        { lat: reading.lat, lng: reading.lng }
      );
      // Only accumulate if the reading is accurate enough
      if (reading.accuracy <= GPS_ACCURACY_THRESHOLD) {
        gpsDistanceRef.current += metersToKm(dist);
        setGpsDistance(gpsDistanceRef.current);
        uploader.setGpsDistance(gpsDistanceRef.current);
      }
    }

    // Update last raw position
    lastRawLatRef.current = reading.lat;
    lastRawLngRef.current = reading.lng;

    // Update UI state (speed, heading, accuracy)
    setCurrentSpeed(reading.speed != null ? mpsToKmh(reading.speed) : null);
    setCurrentHeading(reading.heading);
    setGpsAccuracy(reading.accuracy);

    // Feed into the smart filter
    filter.processReading(reading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGpsError = useCallback((gpsError: GpsError) => {
    setError(gpsError.message);
  }, []);

  // ── GPS tracker ──

  useGpsTracker({
    enabled: gpsEnabled,
    onReading: handleGpsReading,
    onError: handleGpsError,
  });

  // ── Uploader ──

  const handleFlushSuccess = useCallback((_count: number) => {
    setPointsBuffered(uploader.getBufferSize());
    setIsUploadingState(false);
    // Persist session after successful flush
    persistSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistSession]);

  const handleFlushError = useCallback((_errorMsg: string) => {
    setIsUploadingState(false);
    // Don't set error state for upload failures — they're retried automatically.
    // The error state is reserved for GPS-level issues (permission, unavailable).
  }, []);

  const uploader = useTrackingUploader({
    travelId,
    enabled: uploaderEnabled,
    onFlushSuccess: handleFlushSuccess,
    onFlushError: handleFlushError,
  });

  // ── Actions ──

  const start = useCallback(
    async (newTravelId: string, newSegmentId: number): Promise<void> => {
      // Reset all state
      setTravelId(newTravelId);
      setSegmentId(newSegmentId);
      setGpsDistance(0);
      setCurrentSpeed(null);
      setCurrentHeading(null);
      setActiveDuration(0);
      setPointsCollected(0);
      setPointsBuffered(0);
      setGpsAccuracy(null);
      setError(null);
      setWasRestored(false);

      gpsDistanceRef.current = 0;
      lastRawLatRef.current = null;
      lastRawLngRef.current = null;
      activeDurationRef.current = 0;
      pointsCollectedRef.current = 0;

      // Initialize filter
      filter.reset(newSegmentId, 0);

      // Clear any previous buffer
      await uploader.clearBuffer();

      // Persist initial session state
      const sessionState: TrackingSessionState = {
        travelId: newTravelId,
        status: "active",
        segmentId: newSegmentId,
        nextSeq: 0,
        gpsDistance: 0,
        activeDuration: 0,
        lastActiveTimestamp: Date.now(),
        lastRawLat: null,
        lastRawLng: null,
      };
      await storage.setSessionState(sessionState);

      // Start everything
      setStatus("active");
      setGpsEnabled(true);
      setUploaderEnabled(true);
      startDurationTimer();
    },
    [filter, uploader, storage, startDurationTimer]
  );

  const pause = useCallback(async (): Promise<number> => {
    // Force-keep the last GPS point as segment end
    filter.forceKeepLast();
    setPointsBuffered(uploader.getBufferSize());

    // Stop GPS and duration
    setGpsEnabled(false);
    stopDurationTimer();
    setStatus("paused");

    // Flush remaining buffer
    const success = await uploader.flushNow(gpsDistanceRef.current);
    if (!success) {
      // Points are still in buffer — they'll be flushed on resume or stop
    }
    setPointsBuffered(uploader.getBufferSize());

    // Stop uploader
    setUploaderEnabled(false);

    // Persist session state
    const sessionState: TrackingSessionState = {
      travelId: travelId || "",
      status: "paused",
      segmentId,
      nextSeq: filter.getNextSeq(),
      gpsDistance: gpsDistanceRef.current,
      activeDuration: activeDurationRef.current,
      lastActiveTimestamp: Date.now(),
      lastRawLat: lastRawLatRef.current,
      lastRawLng: lastRawLngRef.current,
    };
    await storage.setSessionState(sessionState);

    // Reset speed/heading/accuracy since GPS is stopped
    setCurrentSpeed(null);
    setCurrentHeading(null);
    setGpsAccuracy(null);

    return gpsDistanceRef.current;
  }, [filter, uploader, storage, stopDurationTimer, travelId, segmentId]);

  const resume = useCallback(
    async (newSegmentId: number): Promise<void> => {
      setSegmentId(newSegmentId);
      setError(null);

      // Reset filter for new segment (first point will always be kept)
      filter.resetForNewSegment(newSegmentId, filter.getNextSeq());

      // Restart everything
      setStatus("active");
      setGpsEnabled(true);
      setUploaderEnabled(true);
      startDurationTimer();

      // Persist
      await persistSession();
    },
    [filter, startDurationTimer, persistSession]
  );

  const stop = useCallback(async (): Promise<number> => {
    // Force-keep the last GPS point
    filter.forceKeepLast();
    setPointsBuffered(uploader.getBufferSize());

    // Stop GPS and duration
    setGpsEnabled(false);
    stopDurationTimer();

    // Flush remaining buffer
    await uploader.flushNow(gpsDistanceRef.current);
    setPointsBuffered(uploader.getBufferSize());

    // Stop uploader
    setUploaderEnabled(false);

    // Clear local storage — the server now has everything
    await storage.clearAll();

    const finalDistance = gpsDistanceRef.current;

    // Reset to idle
    setStatus("idle");
    setTravelId(null);
    setCurrentSpeed(null);
    setCurrentHeading(null);
    setGpsAccuracy(null);

    return finalDistance;
  }, [filter, uploader, storage, stopDurationTimer]);

  const discard = useCallback(async (): Promise<void> => {
    // Stop everything
    setGpsEnabled(false);
    setUploaderEnabled(false);
    stopDurationTimer();

    // Clear buffer and session
    await uploader.clearBuffer();
    await storage.clearAll();

    // Reset all state
    setStatus("idle");
    setTravelId(null);
    setSegmentId(0);
    setGpsDistance(0);
    setCurrentSpeed(null);
    setCurrentHeading(null);
    setActiveDuration(0);
    setPointsCollected(0);
    setPointsBuffered(0);
    setGpsAccuracy(null);
    setError(null);
    setWasRestored(false);

    gpsDistanceRef.current = 0;
    lastRawLatRef.current = null;
    lastRawLngRef.current = null;
    activeDurationRef.current = 0;
    pointsCollectedRef.current = 0;
  }, [uploader, storage, stopDurationTimer]);

  const clearRestoredFlag = useCallback(() => {
    setWasRestored(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ── Session restoration on mount ──

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        const savedSession = await storage.getSessionState();
        if (!savedSession || cancelled) return;

        // Session exists — restore it
        setStatus("restoring");
        setTravelId(savedSession.travelId);
        setSegmentId(savedSession.segmentId);

        // Restore distance
        gpsDistanceRef.current = savedSession.gpsDistance;
        setGpsDistance(savedSession.gpsDistance);

        // Restore duration — add elapsed time since last active timestamp
        let restoredDuration = savedSession.activeDuration;
        if (
          savedSession.status === "active" &&
          savedSession.lastActiveTimestamp > 0
        ) {
          const elapsedSinceLastActive =
            (Date.now() - savedSession.lastActiveTimestamp) / 1000;
          // Cap at a reasonable maximum (e.g., 24 hours) to handle
          // cases where the device was off for days
          const cappedElapsed = Math.min(elapsedSinceLastActive, 86400);
          restoredDuration += cappedElapsed;
        }
        activeDurationRef.current = restoredDuration;
        setActiveDuration(Math.floor(restoredDuration));

        // Restore last raw position for distance accumulation
        lastRawLatRef.current = savedSession.lastRawLat;
        lastRawLngRef.current = savedSession.lastRawLng;

        // Restore filter state
        filter.restore(savedSession.segmentId, savedSession.nextSeq);

        // Restore upload buffer
        const restoredBuffer = await uploader.restoreBuffer();
        pointsCollectedRef.current = restoredBuffer.length;
        setPointsCollected(restoredBuffer.length);
        setPointsBuffered(restoredBuffer.length);

        // Update uploader with restored distance
        uploader.setGpsDistance(savedSession.gpsDistance);

        if (cancelled) return;

        setWasRestored(true);

        // Resume based on saved status
        if (savedSession.status === "active") {
          setStatus("active");
          setGpsEnabled(true);
          setUploaderEnabled(true);
          startDurationTimer();
        } else {
          // Was paused — stay paused, user will manually resume
          setStatus("paused");
        }
      } catch (err) {
        // Storage read failed — start fresh
        if (!cancelled) {
          console.warn("[TrackingSession] Failed to restore session:", err);
          setStatus("idle");
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
    // Run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Cleanup on unmount ──

  useEffect(() => {
    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
      }
    };
  }, []);

  // ── Build return value ──

  const session: TrackingSessionState_UI = useMemo(
    () => ({
      status,
      travelId,
      segmentId,
      gpsDistance,
      currentSpeed,
      currentHeading,
      activeDuration,
      pointsCollected,
      pointsBuffered,
      gpsAccuracy,
      isUploading: isUploadingState,
      error,
      wasRestored,
    }),
    [
      status,
      travelId,
      segmentId,
      gpsDistance,
      currentSpeed,
      currentHeading,
      activeDuration,
      pointsCollected,
      pointsBuffered,
      gpsAccuracy,
      isUploadingState,
      error,
      wasRestored,
    ]
  );

  const actions: TrackingSessionActions = useMemo(
    () => ({
      start,
      pause,
      resume,
      stop,
      discard,
      clearRestoredFlag,
      clearError,
    }),
    [start, pause, resume, stop, discard, clearRestoredFlag, clearError]
  );

  return { session, actions };
};